import { LitElement, html, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { map } from 'lit/directives/map.js';
import { Mission, SurveyForm, FormField, FieldType, FormValue, UserSurveyRequest, missionsApi, SurveyApiResponse } from 'lxl-data/missions';
import surveyModalStyles from './lxl-component-survey-modal.scss';
import { store } from 'lxl-data';
import { styleMap } from 'lit/directives/style-map.js';

interface SurveyFormState {
  [questionId: string]: {
    answers: string[];
    openAnswer?: string;
  };
}

@customElement('lxl-component-survey-modal')
export class LxlComponentSurveyModal extends LitElement {
static styles = surveyModalStyles;

  @property({ type: Object })
  mission: Mission;

  @property()
  locale: string;

  @property()
  jwt: string;

  @property()
  onSurveyCompleted: () => void;

  @property({ type: Function })
  localize;

  @state()
  survey: SurveyForm | null = null;

  @state()
  surveyDictionary: { [key: string]: string } = {};

  @state()
  currentQuestionIndex = 0;

  @state()
  formState: SurveyFormState = {};

  @state()
  isLoading = false;

  @state()
  isSubmitting = false;

  @state()
  showThankYou = false;

  @state()
  error: string | null = null;

  @query('lxl-component-modal')
  modalElement: LitElement & { close: () => {} };

  private getSurveyLabel(key: string, args?: (string | number)[]): string {
    let result = this.surveyDictionary[key];

    if (!result && this.localize) {
      result = this.localize(key, args);
    }

    if (args && args.length > 0) {
      args.forEach((arg, index) => {
        result = result.replace(`%{${index}}`, arg.toString());
      });
    }

    return result;
  }

  async connectedCallback() {
    super.connectedCallback();
    await this.loadSurvey();
  }

  async loadSurvey() {
    if (!this.mission?.surveyCode) return;

    this.isLoading = true;
    this.error = null;

    try {
      const result: SurveyApiResponse = await store.dispatch(
        missionsApi.endpoints.getSurvey.initiate({
          jwt: this.jwt,
          locale: this.locale,
          missionCode: this.mission.missionCode
        })
      ).unwrap();

      this.survey = result.survey;
      this.surveyDictionary = result.dictionary || {};
      this.initializeFormState();
    } finally {
      this.isLoading = false;
    }
  }

  initializeFormState() {
    if (!this.survey) return;

    const initialState: SurveyFormState = {};
    this.survey.questions.forEach(question => {
      initialState[question.id] = {
        answers: [],
        openAnswer: ''
      };
    });
    this.formState = initialState;
  }

  getCurrentQuestion(): FormField | null {
    if (!this.survey || this.currentQuestionIndex >= this.survey.questions.length) {
      return null;
    }
    return this.survey.questions[this.currentQuestionIndex];
  }

  isCurrentQuestionValid(): boolean {
    const currentQuestion = this.getCurrentQuestion();
    if (!currentQuestion) return false;

    const questionState = this.formState[currentQuestion.id];
    if (!questionState) return false;

    const isOpenQuestion = currentQuestion.type === FieldType.OPEN;

    if (isOpenQuestion) {
      const openAnswer = questionState.openAnswer?.trim() || '';
      const minLength = currentQuestion.constraint?.minLength || 5;
      const isValid = openAnswer.length >= minLength;

      if (currentQuestion.mandatory && !isValid) {
        return false;
      }

      return isValid;
    }

    const hasSelectedAnswers = questionState.answers.length > 0;

    if (currentQuestion.mandatory && !hasSelectedAnswers) {
      return false;
    }

    if (!hasSelectedAnswers) {
      return false;
    }

    const isMultiple = currentQuestion.type === FieldType.TEXT_MULTIPLE ||
                      currentQuestion.type === FieldType.IMAGE_MULTIPLE ||
                      currentQuestion.type === FieldType.TEXT_MULTIPLE_AND_OPEN;

    if (isMultiple) {
      const maxLength = currentQuestion.constraint?.maxLength;
      const minLength = currentQuestion.constraint?.minLength || 1;

      const exclusiveAnswers = currentQuestion.config?.surveyQuestionConfig?.answers?.filter(
        answer => answer.config?.surveyAnswerConfig?.exclusive === true
      )?.map(answer => answer.id) || [];

      const hasExclusiveSelected = questionState.answers.some(id => exclusiveAnswers.includes(id));

      if (hasExclusiveSelected) {
        return true;
      } else {
        const normalAnswersCount = questionState.answers.filter(id => !exclusiveAnswers.includes(id)).length;

        const meetsMinimum = normalAnswersCount >= minLength;
        const withinMaximum = !maxLength || normalAnswersCount <= maxLength;

        if (!meetsMinimum || !withinMaximum) {
          return false;
        }
      }
    }


    const selectedAnswers = currentQuestion.config?.surveyQuestionConfig?.answers?.filter(
      answer => questionState.answers.includes(answer.id)
    ) || [];

    const hasTextAndOpenAnswer = selectedAnswers.some(
      answer => answer.type === FieldType.TEXT_AND_OPEN_ANSWER
    );

    if (hasTextAndOpenAnswer) {
      const openAnswer = questionState.openAnswer?.trim() || '';
      const textAnswer = selectedAnswers.find(answer => answer.type === FieldType.TEXT_AND_OPEN_ANSWER);
      const minLength = textAnswer?.constraint?.minLength || 5;
      return openAnswer.length >= minLength;
    }

    return true;
  }

  handleAnswerChange(questionId: string, answerId: string, checked: boolean) {
    const currentQuestion = this.getCurrentQuestion();
    if (!currentQuestion) return;

    const isSingleSelect = currentQuestion.type === FieldType.TEXT_SINGLE ||
                          currentQuestion.type === FieldType.IMAGE_SINGLE;

    const newFormState = { ...this.formState };

    const selectedAnswer = currentQuestion.config?.surveyQuestionConfig?.answers?.find(
      answer => answer.id === answerId
    );

    const isExclusive = selectedAnswer?.config?.surveyAnswerConfig?.exclusive === true;

    const exclusiveAnswers = currentQuestion.config?.surveyQuestionConfig?.answers?.filter(
      answer => answer.config?.surveyAnswerConfig?.exclusive === true
    )?.map(answer => answer.id) || [];

    if (isSingleSelect) {
      newFormState[questionId] = {
        ...newFormState[questionId],
        answers: checked ? [answerId] : []
      };
    } else {
      const currentAnswers = newFormState[questionId].answers;

      if (checked) {
        if (isExclusive) {
          newFormState[questionId] = {
            ...newFormState[questionId],
            answers: [answerId]
          };
        } else {
          const filteredAnswers = currentAnswers.filter(id => !exclusiveAnswers.includes(id));

          const maxLength = currentQuestion.constraint?.maxLength;

          if (maxLength && filteredAnswers.length >= maxLength) {
            console.warn(`Puoi selezionare massimo ${maxLength} risposte`);
            return;
          }

          newFormState[questionId] = {
            ...newFormState[questionId],
            answers: [...filteredAnswers, answerId]
          };
        }
      } else {
        newFormState[questionId] = {
          ...newFormState[questionId],
          answers: currentAnswers.filter(id => id !== answerId)
        };
      }
    }

    this.formState = newFormState;
  }


  handleOpenAnswerChange(questionId: string, value: string) {
    this.formState = {
      ...this.formState,
      [questionId]: {
        ...this.formState[questionId],
        openAnswer: value
      }
    };
  }

  handleNext() {
    if (!this.isCurrentQuestionValid()) return;

    if (this.currentQuestionIndex < this.survey!.questions.length - 1) {
      this.currentQuestionIndex++;
      this.requestUpdate();
    } else {
      this.submitSurvey();
    }
  }

  async submitSurvey() {
    if (!this.survey) return;

    this.isSubmitting = true;
    this.error = null;

    try {
      const questions: FormValue[] = this.survey.questions.map(question => ({
        id: question.id,
        value: {
          answers: this.formState[question.id].answers.length > 0 ? this.formState[question.id].answers : null,
          openAnswer: this.formState[question.id].openAnswer || null
        }
      }));

      const userSurveyRequest: UserSurveyRequest = {
        questions
      };

      await store.dispatch(
        missionsApi.endpoints.submitSurveyAnswers.initiate({
          jwt: this.jwt,
          locale: this.locale,
          missionCode: this.mission.missionCode,
          answers: userSurveyRequest,
        })
      ).unwrap();

      this.showThankYou = true;
      this.requestUpdate();
    } catch (error) {
      this.error = 'Failed to submit survey. Please try again.';
    } finally {
      this.isSubmitting = false;
    }
  }

  renderQuestion(question: FormField) {
    const questionState = this.formState[question.id];
    const questionConfig = question.config?.surveyQuestionConfig;

    if (!questionConfig) return nothing;

    return html`
      <div class="survey-question">
        <h2 class="survey-question__title">${questionConfig.title}</h2>

        ${this.renderQuestionSubtitle(question)}

        <div class="survey-question__answers">
          ${question.type === FieldType.OPEN
            ? this.renderOpenQuestion(question, questionState)
            : map(questionConfig.answers || [], answer =>
                this.renderAnswer(question, answer, questionState)
              )}
        </div>
      </div>
    `;
  }

  renderQuestionSubtitle(question: FormField) {
    const isMultiple = question.type === FieldType.TEXT_MULTIPLE ||
                      question.type === FieldType.IMAGE_MULTIPLE ||
                      question.type === FieldType.TEXT_MULTIPLE_AND_OPEN;

    const isSingle = question.type === FieldType.TEXT_SINGLE ||
                    question.type === FieldType.IMAGE_SINGLE;

    if (isMultiple) {
      const maxLength = question.constraint?.maxLength;

      if (maxLength) {
        return html`<p class="survey-question__subtitle">${this.getSurveyLabel('LXL_WIDGET_SURVEY_MAX_ANSWERS', [maxLength])}</p>`;
      } else {
        return html`<p class="survey-question__subtitle">${this.getSurveyLabel('LXL_WIDGET_SURVEY_SUBTITLE_MULTIPLE')}</p>`;
      }
    } else if (isSingle) {
      return html`<p class="survey-question__subtitle">${this.getSurveyLabel('LXL_WIDGET_SURVEY_SUBTITLE_SINGLE')}</p>`;
    }

    return nothing;
  }

  renderAnswer(question: FormField, answer: FormField, questionState: any) {
  const answerConfig = answer.config?.surveyAnswerConfig;
  if (!answerConfig) return nothing;

  const isChecked = questionState?.answers?.includes(answer.id) || false;
  const isImage = answer.type === FieldType.IMAGE_ANSWER;
  const isTextAndOpen = answer.type === FieldType.TEXT_AND_OPEN_ANSWER;

  const inputType = question.type === FieldType.TEXT_SINGLE ||
                  question.type === FieldType.IMAGE_SINGLE ? 'radio' : 'checkbox';

  const hasNoImage = isImage && !answerConfig.image;

  const isMultiple = question.type === FieldType.TEXT_MULTIPLE ||
                    question.type === FieldType.IMAGE_MULTIPLE ||
                    question.type === FieldType.TEXT_MULTIPLE_AND_OPEN;

  const maxLength = question.constraint?.maxLength;
  const exclusiveAnswers = question.config?.surveyQuestionConfig?.answers?.filter(
    answer => answer.config?.surveyAnswerConfig?.exclusive === true
  )?.map(answer => answer.id) || [];

  const currentNormalAnswers = questionState?.answers?.filter(id => !exclusiveAnswers.includes(id)) || [];

  const shouldDisable = isMultiple &&
                      maxLength &&
                      !isChecked &&
                      currentNormalAnswers.length >= maxLength &&
                      !exclusiveAnswers.includes(answer.id);

  if (isImage) {
    return html`
      <div class="survey-answer survey-answer--image">
        <label class="survey-answer__label ${shouldDisable ? 'survey-answer__label--disabled' : ''}">
          <input
            type="${inputType}"
            name="question-${question.id}"
            value="${answer.id}"
            .checked=${isChecked}
            ?disabled=${shouldDisable}
            @change=${(e: Event) => {
              const target = e.target as HTMLInputElement;
              this.handleAnswerChange(question.id, answer.id, target.checked);
            }}
          />
          <div class="survey-answer__image-container ${hasNoImage ? 'survey-answer__image-container--no-image' : ''} ${shouldDisable ? 'survey-answer__image-container--disabled' : ''}">
            ${!hasNoImage
              ? html`
                  <img src="${answerConfig.image}" alt="${answerConfig.text}" class="survey-answer__image" />
                  <span class="survey-answer__text">${answerConfig.text}</span>
                `
              : html`
                  <span class="survey-answer__text">${answerConfig.text}</span>
                `
            }
          </div>
        </label>
      </div>
    `;
  } else {
    return html`
      <div class="survey-answer survey-answer--text">
        <label class="survey-answer__label ${shouldDisable ? 'survey-answer__label--disabled' : ''}">
          <div class="survey-answer__main-content">
            <input
              type="${inputType}"
              name="question-${question.id}"
              value="${answer.id}"
              .checked=${isChecked}
              ?disabled=${shouldDisable}
              @change=${(e: Event) => {
                const target = e.target as HTMLInputElement;
                this.handleAnswerChange(question.id, answer.id, target.checked);
              }}
            />
            <span class="survey-answer__text">${answerConfig.text}</span>
          </div>
        </label>
        
        ${isTextAndOpen && isChecked
          ? html`
              <textarea
                class="survey-answer__open-input"
                placeholder="${this.getSurveyLabel('LXL_WIDGET_SURVEY_OPEN_QUESTION_PLACEHOLDER', [answer.constraint?.minLength || 5])}"
                .value=${questionState?.openAnswer || ''}
                @input=${(e: Event) => {
                  const target = e.target as HTMLTextAreaElement;
                  this.handleOpenAnswerChange(question.id, target.value);
                }}
                maxlength="${answer.constraint?.maxLength || 2000}"
              ></textarea>
            `
          : nothing
        }
      </div>
    `;
  }
}

  renderOpenQuestion(question: FormField, questionState: any) {
    const minLength = question.constraint?.minLength || 5;

    return html`
      <textarea
        class="survey-open-question"
        placeholder="${this.getSurveyLabel('LXL_WIDGET_SURVEY_OPEN_QUESTION_PLACEHOLDER', [minLength])}"
        .value=${questionState?.openAnswer || ''}
        @input=${(e: Event) => {
          const target = e.target as HTMLTextAreaElement;
          this.handleOpenAnswerChange(question.id, target.value);
        }}
        maxlength="${question.constraint?.maxLength || 2000}"
      ></textarea>
    `;
  }

  renderThankYou() {
    return html`
      <div class="survey-thank-you">
        <h2>${this.getSurveyLabel('LXL_WIDGET_SURVEY_THANK_YOU_TITLE')}</h2>
        <p>
          ${this.getSurveyLabel('LXL_WIDGET_SURVEY_THANK_YOU_DESCRIPTION', [this.mission.points])}
        </p>
        <lxl-component-button
          @click=${() => {
          if (this.onSurveyCompleted) {
            this.onSurveyCompleted();
          }
          this.modalElement.close();
        }}
          class="survey-thank-you__button"
        >
          ${this.getSurveyLabel('LXL_WIDGET_SURVEY_BACK_TO_PROGRAM')}
        </lxl-component-button>
      </div>
    `;
  }

  
private getRoundedProgress(): number {
  if (!this.survey) return 0;

  if (this.currentQuestionIndex === 0) {
    return 0;
  }
  
  const completedQuestions = this.currentQuestionIndex;
  const totalQuestions = this.survey.questions.length;
  const rawProgress = (completedQuestions / totalQuestions) * 100;

  let roundedProgress = Math.ceil(rawProgress / 5) * 5;
  if (this.currentQuestionIndex === totalQuestions - 1 && !this.showThankYou) {
    roundedProgress = Math.min(roundedProgress, 95);
  }
  
  const finalProgress = Math.min(Math.max(roundedProgress, 0), 100);
  return isNaN(finalProgress) ? 0 : finalProgress;
}

  render() {
    if (this.isLoading) {
      return html`
        <lxl-component-modal prevent-backdrop-close>
          <div class="survey-modal survey-modal--loading">
            <div class="survey-loading">${this.getSurveyLabel('LXL_WIDGET_SURVEY_LOADING')}</div>
          </div>
        </lxl-component-modal>
      `;
    }

    if (this.error) {
      return html`
        <lxl-component-modal prevent-backdrop-close>
          <div class="survey-modal survey-modal--error">
            <div class="survey-error">
              <h3>${this.getSurveyLabel('LXL_WIDGET_SURVEY_ERROR_TITLE')}</h3>
              <p>${this.getSurveyLabel('LXL_WIDGET_SURVEY_ERROR_MESSAGE')}</p>
              <lxl-component-button @click=${() => this.modalElement.close()}>
                ${this.getSurveyLabel('LXL_WIDGET_SURVEY_CLOSE')}
              </lxl-component-button>
            </div>
          </div>
        </lxl-component-modal>
      `;
    }

    if (this.showThankYou) {
      return html`
        <lxl-component-modal prevent-backdrop-close>
          <div class="survey-modal survey-modal--thank-you">
            ${this.renderThankYou()}
          </div>
        </lxl-component-modal>
      `;
    }

    if (!this.survey) {
      return nothing;
    }

    const currentQuestion = this.getCurrentQuestion();
    if (!currentQuestion) {
      return nothing;
    }

    

    const progress = this.getRoundedProgress();
    const isLastQuestion = this.currentQuestionIndex === this.survey.questions.length - 1;
    const displayProgress = this.showThankYou ? 100 : progress;

    return html`
      <lxl-component-modal prevent-backdrop-close>
        <div class="survey-modal">
          <div class="survey-header">
            ${this.survey.image ? html`
            <div class="survey-header-image">
              <img src="${this.survey.image}" alt="${this.survey.title}" class="survey-header__image" />
            </div>
          ` : nothing}
  
          <div class="survey-title-container">
            <h1 class="survey-title">${this.survey.title}</h1>
            <div class="survey-progress-percentage">
              <span class="survey-progress-percentage__value">${displayProgress}%</span>
              <span class="survey-progress-percentage__text">${this.getSurveyLabel('LXL_WIDGET_SURVEY_COMPLETED_PERCENTAGE')}</span>
            </div>
          </div>
          
          <div class="survey-progress-bar">
            <div class="survey-progress-bar__track">
              <div 
                class="survey-progress-bar__fill" 
                style=${styleMap({
                  width: `${displayProgress}%`
                })}
              ></div>
            </div>
          </div>
        </div>

          <div class="survey-content">
            ${this.renderQuestion(currentQuestion)}
          </div>

          <div class="survey-footer">
            <lxl-component-button
                @click=${this.handleNext}
                ?disabled=${!this.isCurrentQuestionValid() || this.isSubmitting}
                class="survey-next-button"
            >
            ${this.isSubmitting
              ? this.getSurveyLabel('LXL_WIDGET_SURVEY_SUBMITTING')
              : isLastQuestion
              ? this.getSurveyLabel('LXL_WIDGET_SURVEY_SUBMIT')
              : this.getSurveyLabel('LXL_COMPONENTS_NEXT')}
            </lxl-component-button>
          </div>
        </div>
      </lxl-component-modal>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'lxl-component-survey-modal': LxlComponentSurveyModal;
  }
}